"use client";

import React from "react";
import { Quote } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
function FadeInBlock({
  children,
  className = "",
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`} style={{
    transitionDelay: `${delay}ms`
  }}>
      {children}
    </div>;
}
export default function TheMorganneLegacy() {
  return <section id="the-morganne-legacy" className="bg-background py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        
        <FadeInBlock className="text-center mb-16 md:mb-24">
          <h2 className="font-default text-3xl md:text-5xl font-semibold text-foreground mb-6 tracking-tight">
            The Heart Behind the Protocol
          </h2>
          <div className="w-24 h-1 bg-[var(--color-green-accent)] mx-auto rounded-full mb-8"></div>
          <p className="font-default text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Every profound movement begins with a single, unwavering spirit. For us, that spirit was Morganne "Mo" Crain Wright.
          </p>
        </FadeInBlock>

        
        <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16 mb-20 md:mb-32">
          <FadeInBlock className="w-full md:w-1/2 order-2 md:order-1">
            <div className="relative group">
              <div className="absolute -inset-4 bg-secondary/30 backdrop-blur-sm border border-border/50 rounded-2xl rounded-br-4xl transform rotate-3 transition-transform group-hover:rotate-1 opacity-50"></div>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/momo 2.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/momo 2.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/momo 2.jpg-wvc-sizes" alt="Morganne Crain Wright in a serene wellness setting" className="relative rounded-2xl rounded-br-4xl shadow-lg object-cover w-full aspect-[2/3] z-10" loading="lazy" />
            </div>
          </FadeInBlock>
          
          <FadeInBlock className="w-full md:w-1/2 order-1 md:order-2" delay={200}>
            <h3 className="font-default text-2xl md:text-3xl font-semibold text-foreground mb-6">
              A Force of Nature
            </h3>
            <div className="space-y-6 font-default text-lg text-foreground/80 leading-relaxed">
              <p>
                Morganne was never one to accept the status quo. When faced with her own complex health challenges, she quickly realized the fragmented nature of traditional care systems. She wasn't just a patient; she was a seeker of truth in a landscape that often offered only partial answers.
              </p>
              <p>
                Her journey wasn't merely about finding a cure for herself—it became a relentless pursuit to understand the deep, intricate connections between physical vitality, mental clarity, and emotional resilience. She believed that true wellness could not be compartmentalized.
              </p>
            </div>
          </FadeInBlock>
        </div>

        
        <FadeInBlock className="my-20 md:my-32 relative">
          <div className="absolute left-1/2 -translate-x-1/2 -top-8 text-[var(--color-green-accent)]/20">
            <Quote size={80} className="rotate-180" />
          </div>
          <blockquote className="relative z-10 text-center max-w-4xl mx-auto">
            <p className="font-default text-2xl md:text-4xl italic text-foreground leading-snug mb-6">
              Healing is not a destination we reach, but a protocol we practice daily. It requires the courage to look at the whole picture, not just the broken parts.
            </p>
            <footer className="font-default text-sm font-bold tracking-widest text-[var(--color-green-accent)] uppercase">
              — Kristen Chandler - M / O Founder
            </footer>
          </blockquote>
        </FadeInBlock>

        
        <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16 mb-20 md:mb-32">
          <FadeInBlock className="w-full md:w-1/2" delay={200}>
            <h3 className="font-default text-2xl md:text-3xl font-semibold text-foreground mb-6">
              The Genesis of M/O
            </h3>
            <div className="space-y-6 font-default text-lg text-foreground/80 leading-relaxed">
              <p>
                Through years of meticulous research, trial, and profound personal insight, Mo began to codify her approach. She documented what worked, what failed, and most importantly, <strong className="text-foreground font-semibold">why</strong>. This wasn't just a collection of habits; it was a structured, adaptable framework.
              </p>
              <p>
                The M/O Protocol was born from this legacy. It stands as a living testament to her vision—a comprehensive system designed to guide individuals toward their optimal state of being. We founded this organization not just to share her methods, but to honor the profound empathy and rigorous science she brought to the art of healing.
              </p>
            </div>
          </FadeInBlock>

          <FadeInBlock className="w-full md:w-1/2">
            <div className="relative group">
              <div className="absolute -inset-4 bg-accent/30 backdrop-blur-sm border border-border/50 rounded-2xl rounded-br-4xl transform -rotate-3 transition-transform group-hover:-rotate-1 opacity-50"></div>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/momo.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/momo.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/momo.jpg-wvc-sizes" alt="Morganne with family" className="relative rounded-2xl rounded-br-4xl shadow-lg object-cover w-full aspect-[2/3] z-10" loading="lazy" />
            </div>
          </FadeInBlock>
        </div>

        
        <FadeInBlock className="text-center max-w-3xl mx-auto mt-16 md:mt-24">
          <Separator className="mb-12 bg-border/50" />
          <p className="font-default text-xl text-muted-foreground leading-relaxed">
            Today, every protocol we design, every life we touch, carries a piece of Mo's enduring legacy. We continue the work she started, ensuring that her light continues to guide the way forward.
          </p>
        </FadeInBlock>
      </div>
    </section>;
}